import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import store from '../../../redux/store';
import {setStartingQuarter, showToast} from '../../../redux/actions';
import {
  selectGameResults,
  selectGameType,
  selectHexes,
  selectIsGameStarted,
  selectPhase,
  selectPlayers,
  selectStartingQuarters,
  selectTimerEnabled,
  selectTurnDurationMs,
  selectTurnEndTimestamp,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {selectServerTimeDelta} from "../../../redux/reducers/privateStateReducer/selectors";
import {GAME_TYPES} from "../../../constants/game";
import {PHASES} from "../../../constants/phases";
import {
  getRandomHexCoordsForStartingQuarter
} from "../../../utils/gameState/setup/getRandomHexCoordsForStartingQuarter";
import {handleCompleteTurn} from "../../../utils/gameState/turn/handleCompleteTurn";

// Клиентский аналог серверного таймера хода (см. startTurnTimer/handleCompleteTurn
// в server/index.js) — используется только в локальной игре, когда опция
// "Таймер хода" включена на HotSeatPlayersInitPage (по умолчанию выключена).
// Ход закрывается ровно в срок, без серверной отсрочки
// (TURN_AUTO_END_GRACE_MS): она дает действию доехать по сети, а здесь сети нет
const LocalTurnTimer = () => {
  const gameType = useSelector(selectGameType);
  const timerEnabled = useSelector(selectTimerEnabled);
  const isGameStarted = useSelector(selectIsGameStarted);
  const gameResults = useSelector(selectGameResults);
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const turnEndTimestamp = useSelector(selectTurnEndTimestamp);
  // Время хода — общим правилом (см. selectTurnDurationMs): в локальной партии
  // персонального времени не бывает, и это стандартное время фазы с учетом
  // быстрого режима
  const turnDurationMs = useSelector(selectTurnDurationMs);
  const serverTimeDelta = useSelector(selectServerTimeDelta);

  useEffect(() => {
    if (gameType !== GAME_TYPES.LOCAL || !timerEnabled) {
      return;
    }

    if (!isGameStarted || (gameResults && gameResults.length > 0) || !turnEndTimestamp) {
      return;
    }

    const phaseDurationMs = turnDurationMs;
    if (!phaseDurationMs) {
      return;
    }

    const now = Date.now() + serverTimeDelta;
    const msUntilTimeout = Math.max(0, (turnEndTimestamp + phaseDurationMs) - now);

    // Сообщение об отмене невалидных строений (SEND_CONSTRUCTION_VALIDATION_MESSAGE)
    // в онлайн-игре превращает в тост playEffectForServerAction по сетевому
    // сообщению сервера — в локальной игре сервера нет, поэтому ловим экшен здесь
    const localTimeoutStoreWrapper = {
      getState: () => store.getState(),
      dispatch: (action) => {
        store.dispatch(action);
        if (action.type === 'SEND_CONSTRUCTION_VALIDATION_MESSAGE') {
          store.dispatch(showToast(action.payload.validationMessage, 'error', 15000, false, true));
        }
      }
    };

    const handleTurnTimeout = () => {
      const state = store.getState();
      const currentGameResults = selectGameResults(state);
      if (currentGameResults && currentGameResults.length > 0) {
        return;
      }

      // Фаза расстановки — особый случай: handleCompleteTurn ничего не знает
      // про стартовые кварталы, поэтому сначала отставшим игрокам расставляются
      // случайные (по аналогии с сервером), а затем фаза принудительно
      // завершается — для локальной игры завершение SETUP идёт по одному
      // игроку за раз (как обычные ходы), поэтому доводим её до конца циклом
      if (selectPhase(state) === PHASES.SETUP) {
        const hexes = selectHexes(state);
        const players = selectPlayers(state);
        const startingQuarters = selectStartingQuarters(state);

        players.forEach((player) => {
          if (!startingQuarters[player.id]) {
            const randomHexKey = getRandomHexCoordsForStartingQuarter(hexes, player);
            if (randomHexKey) {
              store.dispatch(setStartingQuarter(player.id, randomHexKey));
            }
          }
        });

        let iterations = 0;
        while (selectPhase(store.getState()) === PHASES.SETUP && iterations <= players.length) {
          handleCompleteTurn(localTimeoutStoreWrapper, false);
          iterations++;
        }
        return;
      }

      // Остальные фазы — как серверный таймаут: принудительное завершение хода
      // (isClientExecutor=false) само валидирует и отменяет невалидные строения
      // фазы строительства (см. handleCompleteTurn)
      handleCompleteTurn(localTimeoutStoreWrapper, false);
    };

    const timeoutId = setTimeout(handleTurnTimeout, msUntilTimeout);
    return () => clearTimeout(timeoutId);
  }, [gameType, timerEnabled, isGameStarted, gameResults, phase, warPhaseStage, turnEndTimestamp, turnDurationMs, serverTimeDelta]);

  return null;
};

export default LocalTurnTimer;
